<template>
    <div>
        <!-- Province — filter only, not saved -->
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.cityProvince")
                }}</label>
                <Select
                    v-model="selected_province"
                    :placeholder="$t('phumVillage.cityProvince')"
                    filterable
                    @on-change="onProvinceChange"
                >
                    <Option
                        v-for="province in provinces"
                        :key="province.province_code"
                        :value="province.province_code"
                    >
                        {{ province.name_en }}
                    </Option>
                </Select>
            </div>
        </div>

        <!-- District — filter only, not saved -->
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.khanDistrict")
                }}</label>
                <Select
                    v-model="selected_district"
                    :placeholder="$t('phumVillage.khanDistrict')"
                    :disabled="!selected_province && !isUpdate"
                    filterable
                    @on-change="onDistrictChange"
                >
                    <Option
                        v-for="district in districts"
                        :key="district.district_code"
                        :value="district.district_code"
                    >
                        {{ district.name_en }}
                    </Option>
                </Select>
            </div>
        </div>

        <!-- Commune — saved -->
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("phumVillage.sangkatCommune")
                }}</label>
                <Select
                    v-model="model.commune_code"
                    :placeholder="$t('phumVillage.sangkatCommune')"
                    :class="{ 'is-invalid': errors.has('commune_code') }"
                    :disabled="!selected_district && !isUpdate"
                    filterable
                >
                    <Option
                        v-for="commune in communes"
                        :key="commune.commune_code"
                        :value="commune.commune_code"
                    >
                        {{ commune.name_en }}
                    </Option>
                </Select>
                <div class="invalid-feedback" v-if="errors.has('commune_code')">
                    {{ errors.first("commune_code") }}
                </div>
            </div>
        </div>

        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("phumVillage.nameKh")
                }}</label>
                <input
                    v-model.trim="model.name_kh"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('name_kh') }"
                    ref="name_kh"
                />
                <div class="invalid-feedback" v-if="errors.has('name_kh')">
                    {{ errors.first("name_kh") }}
                </div>
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label required">{{
                    $t("phumVillage.nameEn")
                }}</label>
                <input
                    v-model.trim="model.name_en"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('name_en') }"
                />
                <div class="invalid-feedback" v-if="errors.has('name_en')">
                    {{ errors.first("name_en") }}
                </div>
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.villageChiefName")
                }}</label>
                <input
                    v-model.trim="model.village_chief_name"
                    type="text"
                    class="form-control"
                />
            </div>
        </div>
        <div class="mb-3">
            <div class="col-md-12">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.villagePhoneNumber")
                }}</label>
                <input
                    v-model.trim="model.village_phone_number"
                    type="text"
                    class="form-control"
                />
            </div>
        </div>
        <div class="row mb-3">
            <div class="col-md-4">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.numberOfFamily")
                }}</label>
                <input
                    v-model.number="model.number_of_family"
                    type="number"
                    class="form-control"
                />
            </div>
            <div class="col-md-4">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.totalMale")
                }}</label>
                <input
                    v-model.number="model.total_male"
                    type="number"
                    class="form-control"
                />
            </div>
            <div class="col-md-4">
                <label class="form-col-label control-label">{{
                    $t("phumVillage.totalFemale")
                }}</label>
                <input
                    v-model.number="model.total_female"
                    type="number"
                    class="form-control"
                />
            </div>
        </div>
        <div slot="footer">
            <div class="tw-flex tw-justify-end tw-space-x-2 tw-sh">
                <ts-button
                    @click.prevent="() => $emit('cancel')"
                    class="btn-gray"
                    >{{ $t("cancel") }}</ts-button
                >
                <ts-button
                    v-if="!isUpdate"
                    color="primary"
                    outline
                    :waiting="waiting_new"
                    :disabled="waiting_new || waiting"
                    @click.prevent="onSaveAddNew"
                    >{{ $t("saveAddNew") }}</ts-button
                >
                <ts-button
                    color="primary"
                    v-if="!isUpdate"
                    :waiting="waiting"
                    :disabled="waiting_new || waiting"
                    @click.prevent="onSave"
                    >{{ $t("save") }}</ts-button
                >
                <ts-button
                    v-if="isUpdate"
                    color="primary"
                    @click.prevent="onUpdate"
                    :waiting="waiting"
                    >{{ $t("update") }}</ts-button
                >
            </div>
        </div>
        <Spin size="large" fix v-if="loading"> </Spin>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapState } from "vuex";
import { isEmpty } from "lodash";

export default {
    name: "villageForm",
    data() {
        return {
            errors: new Errors(),
            loading: false,
            waiting: false,
            waiting_new: false,
            provinces: [],
            districts: [],
            communes: [],
            selected_province: null,
            selected_district: null,
            model: {
                commune_code: null,
                name_kh: null,
                name_en: null,
                village_chief_name: null,
                village_phone_number: null,
                number_of_family: null,
                total_male: null,
                total_female: null
            }
        };
    },
    computed: {
        ...mapState("corporate/phumVillage", ["edit_data"]),
        isUpdate() {
            return !isEmpty(this.edit_data);
        }
    },
    methods: {
        fetchProvinces() {
            this.$store
                .dispatch("corporate/phumVillage/getCityProvince")
                .then(response => {
                    this.provinces = response.data;
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                });
        },
        fetchDistricts(province_code) {
            this.$store
                .dispatch("corporate/phumVillage/getKhanDistrict", {
                    province_code
                })
                .then(response => {
                    this.districts = response.data;
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                });
        },
        fetchCommunes(district_code) {
            this.$store
                .dispatch("corporate/phumVillage/getSangkatCommune", {
                    district_code
                })
                .then(response => {
                    this.communes = response.data;
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                });
        },
        onProvinceChange(province_code) {
            this.selected_district = null;
            this.model.commune_code = null;
            this.districts = [];
            this.communes = [];
            this.fetchDistricts(province_code);
        },
        onDistrictChange(district_code) {
            this.model.commune_code = null;
            this.communes = [];
            this.fetchCommunes(district_code);
        },
        async onSaveAddNew() {
            this.errors = new Errors();
            this.waiting_new = true;
            this.$store
                .dispatch("corporate/phumVillage/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("fetchData");
                    this.clearInput();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting_new = false;
                });
        },
        async onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("corporate/phumVillage/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        onUpdate() {
            this.waiting = true;
            this.$store
                .dispatch("corporate/phumVillage/update", {
                    id: this.edit_data.village_code,
                    data: this.model
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        setEditData() {
            this.fetchProvinces();
            this.$nextTick(function() {
                this.$refs.name_kh.focus();
            });
            if (!isEmpty(this.edit_data)) {
                // derive province from commune_code first 2 digits
                const provinceCode = this.edit_data.commune_code
                    ? this.edit_data.commune_code.substring(0, 2)
                    : null;
                // derive district from commune_code first 4 digits
                const districtCode = this.edit_data.commune_code
                    ? this.edit_data.commune_code.substring(0, 4)
                    : null;

                this.selected_province = provinceCode;
                this.selected_district = districtCode;

                if (provinceCode) this.fetchDistricts(provinceCode);
                if (districtCode) this.fetchCommunes(districtCode);

                this.model.commune_code = this.edit_data.commune_code;
                this.model.name_kh = this.edit_data.name_kh;
                this.model.name_en = this.edit_data.name_en;
                this.model.village_chief_name = this.edit_data.village_chief_name;
                this.model.village_phone_number = this.edit_data.village_phone_number;
                this.model.number_of_family = this.edit_data.number_of_family;
                this.model.total_male = this.edit_data.total_male;
                this.model.total_female = this.edit_data.total_female;
            }
        },
        clearInput() {
            this.errors = new Errors();
            this.selected_province = null;
            this.selected_district = null;
            this.districts = [];
            this.communes = [];
            this.model.commune_code = null;
            this.model.name_kh = null;
            this.model.name_en = null;
            this.model.village_chief_name = null;
            this.model.village_phone_number = null;
            this.model.number_of_family = null;
            this.model.total_male = null;
            this.model.total_female = null;
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "PHUM VILLAGE",
                desc: not.text
            });
        }
    }
};
</script>
